import React, { useState } from 'react';
import { Box, Typography, Popover, Divider, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { format } from 'date-fns';
import { FORECAST_METHODS } from '../../utils/categoryForecast';

/*
  ForecastFigure: the "Fcast: $686 (153%)" figure for a budget/category row,
  colored over/under, and tappable — the popover shows the forecast's terms
  ("spent $412 + $170 Power due 18th + ~$104 at your usual pace = $686") and,
  when the caller can persist it, a per-category picker for how the figure is
  estimated (docs/ACTION-SCHEDULING-DESIGN.md, "Category forecasting").

  Props:
   - terms: result of forecastCategory() (null renders nothing)
   - budgetAmount: the period's target, for the percentage (0 hides it)
   - type: 'Expense' | 'Income' — flips what "over" means
   - prefix: text before the figure (default "Fcast: ")
   - onMethodChange: (method) => void — persists the category's forecast
     method; omitted (e.g. uncategorized rows) hides the picker
   - sx: Typography overrides
*/

// Beyond ~10× the target a forecast percentage reads as a bug ("21345%");
// show a multiple instead.
export const formatForecastPct = (p) => (p >= 1000 ? `${Math.round(p / 100)}×` : `${Math.round(p)}%`);

const fmt = (amt) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amt || 0);

const TermRow = ({ label, value, muted = true, bold = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
    <Typography variant="caption" color={muted ? 'text.secondary' : 'text.primary'} sx={{ fontWeight: bold ? 700 : 400 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: bold ? 700 : 600, flexShrink: 0 }}>
      {value}
    </Typography>
  </Box>
);

const MAX_COMMITTED_ROWS = 5;

const methodBasis = (terms) => {
  if (terms.isPast) return 'Completed period, showing actual figures.';
  if (terms.method === 'pace' || (terms.method === 'typical' && terms.usedFallback)) {
    const rate = terms.daysElapsed > 0 ? terms.spent / terms.daysElapsed : 0;
    const pace = `Current pace: ${fmt(rate)}/day so far, carried through the ${terms.daysRemaining} day${terms.daysRemaining === 1 ? '' : 's'} left.`;
    return terms.usedFallback
      ? `No completed periods yet, so Typical month falls back to pace. ${pace}`
      : pace;
  }
  if (terms.method === 'typical') {
    return `Median of your last ${terms.history.periods} completed period${terms.history.periods === 1 ? '' : 's'}, so one-off spikes don't move it. Never below what's already spent or scheduled.`;
  }
  // 'auto'
  return !terms.history
    ? "This period's pace only, since there are no completed periods to compare against yet."
    : terms.history.basis === 'same-month-last-year'
      ? 'Remainder estimated from what landed after this point in the same month last year (12+ months of history).'
      : `Remainder blends this period's pace with what usually lands after this point, from your last ${terms.history.periods} completed period${terms.history.periods === 1 ? '' : 's'}${terms.isLumpy ? '. Spending here is lumpy, so history carries extra weight' : ''}.`;
};

const remainderLabel = (method, usedFallback) =>
  method === 'typical' && !usedFallback
    ? 'To your typical month'
    : method === 'pace' || usedFallback
      ? "At this period's pace"
      : 'At your usual pace';

const ForecastFigure = ({ terms, budgetAmount = 0, type = 'Expense', prefix = 'Fcast: ', onMethodChange, sx }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!terms) return null;

  const pct = budgetAmount > 0 ? (terms.forecast / budgetAmount) * 100 : null;
  const color =
    pct === null
      ? 'text.secondary'
      : type === 'Income'
        ? (pct >= 100 ? 'success.main' : 'warning.main')
        : (pct > 101 ? 'error.main' : 'success.main');

  const basis = methodBasis(terms);

  const committedShown = terms.committedItems.slice(0, MAX_COMMITTED_ROWS);
  const committedHidden = terms.committedItems.length - committedShown.length;

  return (
    <>
      <Typography
        variant="caption"
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }
        }}
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 500,
          color,
          cursor: 'pointer',
          textDecoration: 'underline dotted',
          textUnderlineOffset: 3,
          whiteSpace: 'nowrap',
          '&:hover': { opacity: 0.75 },
          ...sx,
        }}
      >
        {prefix}{fmt(terms.forecast)}{pct !== null ? ` (${formatForecastPct(pct)})` : ''}
      </Typography>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={(e) => { e?.stopPropagation?.(); setAnchorEl(null); }}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 280 }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            How this forecast is built
          </Typography>
          <TermRow label={type === 'Income' ? 'Received so far' : 'Spent so far'} value={fmt(terms.spent)} />
          {committedShown.map((item, i) => (
            <TermRow key={i} label={`${item.name} · due ${format(item.date, 'd MMM')}`} value={fmt(item.amount)} />
          ))}
          {committedHidden > 0 && (
            <TermRow label={`+${committedHidden} more scheduled`} value="" />
          )}
          {!terms.isPast && (
            <TermRow label={remainderLabel(terms.method, terms.usedFallback)} value={`~${fmt(terms.remainder)}`} />
          )}
          <Divider sx={{ my: 0.5 }} />
          <TermRow
            label="Forecast"
            value={`${fmt(terms.forecast)}${budgetAmount > 0 ? ` of ${fmt(budgetAmount)}` : ''}`}
            muted={false}
            bold
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: '0.7rem', lineHeight: 1.4 }}>
            {basis}
          </Typography>
          {onMethodChange && !terms.isPast && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                Forecast method for this category
              </Typography>
              <ToggleButtonGroup
                value={terms.method}
                exclusive
                size="small"
                fullWidth
                onChange={(e, value) => { if (value) onMethodChange(value); }}
              >
                {FORECAST_METHODS.map((m) => (
                  <ToggleButton key={m.value} value={m.value} sx={{ fontSize: '0.7rem', px: 0.5, py: 0.4, textTransform: 'none' }}>
                    {m.short}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem', lineHeight: 1.4 }}>
                {FORECAST_METHODS.find((m) => m.value === terms.method)?.description}
              </Typography>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ForecastFigure;
